import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { AsyncSubject, BehaviorSubject, Observable, forkJoin} from "rxjs";
import { MeSelectBrick } from "../../components/me-form-group/me-select/me-select.component";
import { MeRequiredValidation } from "../../components/me-validation/me-required-validation";
import { MEError, MEErrorType } from "../../error/me-error";
import { MEEntityAPIResponseHandler, ResponseCode, MEAPIResponse } from "../api/me-api-response";
import { MEAPIService } from "../api/me-api.service";
import { ExternalSettingsService } from "../external-settings/external-settings.service";
import { SortedAccounts, Account, AccountKey, CardSourceType } from "./accounts.interface";
import { InternalSettingsService } from "../internal-settings/internal-settings.service";
import { MeValidation } from "../../components/me-validation/me-validation";
import { CurrencyUtilityService } from "src/app/system/utility/currency-utility.service";
import { AccountHistoryService } from "src/app/business/account-history/account-history.service";
import { MEErrorService } from "../../error/me-error.service";
import { ExternalSettings } from "../external-settings/external-settings.interface";

@Injectable({
  providedIn: 'root',
})
export class AccountsService {

  private readonly API_ACCOUNT: string = MEAPIService.BASE_URL + '/customer/accounts';
  private readonly API_HIDDEN_ACCOUNT: string = MEAPIService.BASE_URL + '/customer/accounts?showHidden=true';

  public loaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public accounts$: BehaviorSubject<SortedAccounts | null> = new BehaviorSubject<SortedAccounts | null>(null);
  private separateCreditCard = false;
  private showCurrency = false;
  private externalSettings!: ExternalSettings;

  constructor(
    private srvApi: MEAPIService,
    private srvES: ExternalSettingsService,
    private srvIS: InternalSettingsService,
    private srvTranslate: TranslateService,
    private srvError: MEErrorService,
  ) {

    // Retrieve the external settings which assume to be ready on loading, the parameter is required for parseAccounts which is async service
    forkJoin([
      this.srvES.externalSettings$
    ]).subscribe(([externalSettings]) => {
      if (!externalSettings) return;

      this.externalSettings = externalSettings;
      this.separateCreditCard = externalSettings.globalVariables.isModuleEnabledCreditCard;
      this.showCurrency = externalSettings.globalVariables.isDisplayCurrency;
    })

    // Retrieve the internal settings which assume to be ready on loading, the parameter is required for parseAccounts which is async service
    forkJoin([
      this.srvIS.getCurrentSettings(),
    ]).subscribe(([is]) => {
      if (!is) {
        this.clearAccount();
      } 

    })
  }

  public buildAccountSelect(index : number, accountList : Account[], required: boolean = true, tips: string[] = [], label: string = 'ACCOUNT_POUND', onSelectionChange: (item: any) => void = (item: any) => {}) : MeSelectBrick {
    const meRequiredValidation = new MeRequiredValidation(this.srvTranslate)
    if (required) {
      return this._buildAccountSelect(index, accountList, [meRequiredValidation], tips, label, onSelectionChange);
    } else {
      return this._buildAccountSelect(index, accountList, [], tips, label, onSelectionChange);

    }
  }

  

  loadAccounts(): Observable<boolean> {
    this.clearAccount();

    return this.srvApi
      .Get(this.API_ACCOUNT, [new SuccessfulAccountHandler(this), new PartialAccountListHandler(this, this.srvError), new NoAccountHandler(this)])
      .pipe(() => {
        return this.loaded$;
      });
  }

  /**
   * Reload accounts (Underlying calling loadAccounts)
   * HANDLE WITH CARE, SOME COMPONENTS will be crashed if the accounts have been subscribe before
   */
  reloadAccounts(): Observable<boolean>{
    return this.loadAccounts();
  }

  /**
   * Mark the account list to be dirty such that it will reload again in Account Resolver,
   * this prevent side effect of ActivityService
   */
  markDirty() {
    this.loaded$.next(false);
  }


  getHiddenAccounts(): AsyncSubject<SortedAccounts> {
    let ret = new AsyncSubject<SortedAccounts>();
    this.srvApi.Get<Account[]>(this.API_HIDDEN_ACCOUNT, [new SuccessfulHiddenAccountHandler<Account[]>(this, ret),  new PartialHiddenAccountListHandler(this, ret, this.srvError)]);
    return ret;
  }
  
  clearAccount(): void {
    this.accounts$.next(null);
    this.loaded$.next(false);
  }

  isEmptyAccount(): boolean {
    let accounts = this.accounts$.getValue();
    return accounts === null || accounts.visibleAccounts.length === 0;
  }

  handleNoAccount() {
    this.loaded$.next(true);
  }

  handleSuccess(accounts: Account[]) {
    let sortedAccounts = this.parseAccounts(accounts);
    this.accounts$.next(sortedAccounts);
    this.loaded$.next(true);
  }

  handlePartialAccounts(accounts: Account[]) {
    let sortedAccounts = this.parseAccounts(accounts);
    this.accounts$.next(sortedAccounts);
    this.loaded$.next(true);
  }

  public parseAccounts(accounts: Account[]) : SortedAccounts {
    let allAccounts: Account[] = [];
    let visible: Account[] = [];
    let deposits: Account[] = [];
    let loans: Account[] = [];
    let ccs: Account[] = [];

    accounts.forEach((account) => {
      if (this.showCurrency) {
        account.displayName = '(' + account.currency + ') ' + account.accountName
      } else {
        account.displayName = account.accountName;
      }
      
      allAccounts.push(account);
      if (!account.isVisible) return;

      visible.push(account);
      if (account.cls == AccountClass.DEPOSIT) {
        deposits.push(account);
        return;
      } else if (this.separateCreditCard && account.group == AccountGroup.CREDIT_CARD) {
        ccs.push(account);
        return;
      } else {
        loans.push(account);
        return;
      }
    });

    let result : SortedAccounts = {
      allAccounts: allAccounts,
      visibleAccounts: visible,
      deposits: deposits,
      loans: loans,
      ccs: ccs,
    };

    return result;
  }

  public static getCheckingAccounts(accounts : SortedAccounts): Account[] {
    let filter = (account: Account) => {
      return account.group == AccountGroup.CHECKING;
    };
    return accounts.visibleAccounts.filter(filter);
  }

  public static getSavingsAccounts(accounts : SortedAccounts): Account[] {
    let filter = (account: Account) => {
      return account.group == AccountGroup.SAVINGS;
    };
    return accounts.visibleAccounts.filter(filter);
  }
  
  public static getDepositAccounts(accounts : SortedAccounts): Account[] {
    return accounts.deposits;
  }

  public static getDebitCardAccounts(accounts : SortedAccounts): Account[] {
    let filter = (account: Account) => {
      return account.group == AccountGroup.DEBIT_CARD;
    };
    return accounts.allAccounts.filter(filter);
  }

  public static getCreditCardAccounts(accounts : SortedAccounts): Account[] {
    return accounts.ccs;
  }

  public static getLoansAccounts(accounts : SortedAccounts): Account[] {
    return accounts.loans;
  }

  public getPostabletAccounts(accounts : SortedAccounts): Account[] {

    // Web 2 Logic
    // account.isPermittedTransferFrom && (account.cls == 'D' || ( !account.isBlocked && account.cls == 'CC' &&  externalSetting.globalVariables.isModuleEnabledCreditCard))

    let filter = (account: Account) => {
      if (!account.isPermittedTransferFrom) return false;
      if (account.isBlocked) return false;

      if (account.cls == AccountClass.DEPOSIT) return true; // Deposit accounts are always postable
      if (account.cls == AccountClass.CREDIT_CARD && this.externalSettings.globalVariables.isModuleEnabledCreditCard) return true; // Credit card accounts are postable if the module is enabled

      return false;
    };

    let list = accounts.visibleAccounts.filter(filter);
    return list;
  }

  public static getSavingsORCheckingAccounts(accounts : SortedAccounts): Account[] {
    let filter = (account: Account) => {
      return account.group == AccountGroup.SAVINGS || account.group == AccountGroup.CHECKING;
    };
    return accounts.visibleAccounts.filter(filter);
  }

  public static isAccountActive(account: Account): boolean {
    return account.status == 'ACTIVE';
  }
    
  public static getAccountName(account: Account) : string {
    return (
        account.accountName +
        (account.isDisplayAlias && account.alias
          ? ' - ' + account.alias
          : ' - ' + account.idAccount) +
        ' ' +
        account.currencySymbol +
        ' ' + 
        CurrencyUtilityService.formatAmountNoSymbol(account.availableBalance)
      );    
  }

  public static getAccountShortName(account: Account) : string {
    return (
        account.accountName + (account.isDisplayAlias && account.alias ? ' - ' + account.alias : ' - ' + account.idAccount)
      );    
  }

  public static getAccount(account: Account) : Account {
    return account;
  }

  public static getAccountGroupDesc(accountType: string) {
    switch (accountType) {
      case AccountGroup.LOAN: return 'LOANS';
      case AccountGroup.CHECKING: return 'CHECKING';
      case AccountGroup.SAVINGS: return 'SAVINGS';
      case AccountGroup.DEPOSIT: return 'LABEL_TEXT_DEPOSIT';
      case AccountGroup.DEBIT_CARD: return 'LABEL_TEXT_DEBIT';
      case AccountGroup.CREDIT_CARD: return 'LABEL_TEXT_CREDIT';
      default: return 'Unknown';
    }
  }

  public static reviewAccount(value: Account) {
    if (!value) {
      return '';
    }
    
    return (
      '(' +
      value.currency +
      ') - ' +
      value.accountKey.accountId +
      ' - ' +
      value.accountName
    );
  }

  public static getLast4Digits(account: Account) : string{
    return account.idAccount.slice(-4);
  }

  public static getMaskCardNumber(account: Account) : string{
    return "**** - **** - **** - " + account.idAccount.slice(-4);
  }

  public static lookupAccountByAccountKeyKey(accountList: Account[], accountKeyKey: string) {
    return accountList?.find((account) =>  account.accountKey.key === accountKeyKey);
  }

  public static lookupAccountByAccountKeyAccountId(accountList: Account[], accountId: string) {
    return accountList?.find((account) =>  account.accountKey.accountId === accountId);
  }

  public static lookupAccountByAccountKey(accountList: Account[], accountKey: AccountKey) {
    return accountList?.find((account) =>  account.accountKey.key === accountKey.key);
  }

  private _buildAccountSelect(index : number, 
                              accountList : Account[], 
                              meRequiredValidation: MeValidation[], 
                              tips: string[], 
                              label: string, 
                              onSelectionChange: (item: any) => void = (item: any) => {}) : MeSelectBrick {
    return new MeSelectBrick('account', index, label, null, accountList, onSelectionChange, AccountsService.getAccountName, AccountsService.getAccount, 
                            meRequiredValidation, tips, false, AccountsService.reviewAccount, undefined, undefined, undefined, undefined, undefined, this.compareFn);
  }

  private compareFn(a1: Account, a2: Account) {
    return a1 && a2 && (a1.accountKey.accountId == a2.accountKey.accountId);
  }


  // To be confirm with John
  public static isCreditCard(account: Account) {
  /*
    $scope.isLoan = account.group == AppConstants.ACCOUNT_GROUP_LOAN ;	
    $scope.isCreditCard = (account.group == AppConstants.ACCOUNT_GROUP_CREDIT_CARD || account.name == 'Credit Card Account');

    $scope.isLoanCreditCard = false;
    if ($scope.isLoan && account.accountName == 'Credit Card Account') {
      $scope.isLoan = false;
      $scope.isCreditCard = false;
      $scope.isLoanCreditCard = true;
    }
  */
    let isLoan = (account.group == AccountGroup.LOAN);
    let isCreditCard = (account.group == AccountGroup.CREDIT_CARD) || (account.accountName == AccountHistoryService.CRED_CARD);

    // If it is a loan, it is not a credit card
    if (isLoan && account.accountName == AccountHistoryService.CRED_CARD) {
      return false;
    }

    return isCreditCard;
  }

  // Deposit is the default "Account Type"
  public static isDeposit(account: Account) {
    let isDefined = AccountsService.isCreditCard(account) ||  AccountsService.isLoan(account) || AccountsService.isFiservLoan(account) || AccountsService.isCreditCard(account);
    return (!isDefined)
  }

  public static isLoanCreditCard(account: Account) {
    let isLoan = (account.group == AccountGroup.LOAN);
    return (isLoan && account.accountName == AccountHistoryService.CRED_CARD && account.processor != AccountHistoryService.FISERV);
  }

  public static isFiservLoan(account: Account) {
    let isLoan = (account.group == AccountGroup.LOAN);
    return (isLoan && account.processor == AccountHistoryService.FISERV);
  }

  public static isLoan(account: Account) {
    let isLoan = (account.group == AccountGroup.LOAN);
    if (isLoan) {
      return !AccountsService.isLoanCreditCard(account) && !AccountsService.isFiservLoan(account);
    }
    return false;
  }

  public static isBevertecAccount(account: Account) {
    return (account.accountKey.sourceType == CardSourceType.BEVERTEC);
  }


}

class SuccessfulAccountHandler implements MEEntityAPIResponseHandler<Account[]>
{
  constructor(private srvAccount: AccountsService) {}

  public readonly code: ResponseCode = ResponseCode.SUCCESS;

  handle(response: MEAPIResponse<Account[]>): boolean | MEError {
    this.srvAccount.handleSuccess(response.entity);

    return true;
  }
}

class PartialAccountListHandler implements MEEntityAPIResponseHandler<Account[]>
{
  constructor(private srvAccount: AccountsService, private srvError: MEErrorService) {}

  public readonly code: ResponseCode = ResponseCode.PARTIAL_ACCOUNT_INFORMATION_UNAVAILABLE;

  handle(response: MEAPIResponse<Account[]>): boolean | MEError {
    this.srvError.showDialog(this.srvError.SERVER_TITLE, [{
      code: response.status.code,
      message: response.message || response.status.message,
      display: MEErrorType.Popup,
      url: ''
    }]);

    this.srvAccount.handlePartialAccounts(response.entity);

    return true;
  }
}

export class SuccessfulHiddenAccountHandler<T> implements MEEntityAPIResponseHandler<Account[]> {
  public readonly code: ResponseCode = ResponseCode.SUCCESS;

  constructor(private srvAccount: AccountsService, private subj : AsyncSubject<SortedAccounts>) {}

  handle(response: MEAPIResponse<Account[]>): boolean | MEError {
    let sortedAccounts = this.srvAccount.parseAccounts(response.entity);
    this.subj.next(sortedAccounts);
    this.subj.complete();
    return true;
  }
}

export class PartialHiddenAccountListHandler<T> implements MEEntityAPIResponseHandler<Account[]> {
  constructor(private srvAccount: AccountsService, private subj : AsyncSubject<SortedAccounts>, private srvError: MEErrorService) {}

  public readonly code: ResponseCode = ResponseCode.PARTIAL_ACCOUNT_INFORMATION_UNAVAILABLE;

  handle(response: MEAPIResponse<Account[]>): boolean | MEError {
    this.srvError.showDialog(this.srvError.SERVER_TITLE, [{
      code: response.status.code,
      message: response.message || response.status.message,
      display: MEErrorType.Popup,
      url: ''
    }]);

    let sortedAccounts = this.srvAccount.parseAccounts(response.entity);
    this.subj.next(sortedAccounts);
    this.subj.complete();

    return true;
  }
}

class NoAccountHandler implements MEEntityAPIResponseHandler<Account[]>
{
  constructor(private srvAccount: AccountsService) {}

  public readonly code: ResponseCode = ResponseCode.NO_ACCOUNT_FOUND;

  handle(response: MEAPIResponse<Account[]>): boolean | MEError {
    this.srvAccount.handleNoAccount();
    return true;
  }
}

export enum AccountGroup {
  LOAN = 'LN',
  CHECKING = 'DDA',
  SAVINGS = 'SAV',
  DEPOSIT = 'CD',
  DEBIT_CARD = 'DC',
  CREDIT_CARD = 'CC',
}


export enum AccountClass {
  DEPOSIT = 'D',
  CREDIT_CARD = 'CC'
}